import { computed, inject, Injectable, signal } from '@angular/core';
import { IQuizEditItem } from '../fi-quiz';
import { cloneDeep } from '@fi-shared/utils';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { AccessRightFilterCollectionModel } from '@fi-app/shared/models/access-right-filter-collection.model';
import { IAccessRightSelectionFiltersCollection } from '@fi-shared/models';
import { FiQuizEditorTabIndex } from '../fi-quiz-enums';
import { INameIdObj } from '@fi-app/shared/fi-shared-interfaces';

@Injectable()
export class FiQuizEditorStoreService {
    private fiContentLanguageService = inject(FiContentLanguageSharedService);

    private readonly _formFailedValidation = signal<boolean>(false);
    readonly formFailedValidation = this._formFailedValidation.asReadonly();
    setFormFailedValidation(formFailedValidation: boolean): void {
        this._formFailedValidation.set(formFailedValidation);
    }

    private readonly _selectedTabIndex = signal<FiQuizEditorTabIndex>(FiQuizEditorTabIndex.QuizEditor);
    readonly selectedTabIndex = this._selectedTabIndex.asReadonly();
    setSelectedTabIndex(selectedTabIndex: FiQuizEditorTabIndex): void {
        this._selectedTabIndex.set(selectedTabIndex);
    }

    private readonly _savedSelectionFilters = signal<IAccessRightSelectionFiltersCollection[]>([]);
    readonly savedSelectionFilters = this._savedSelectionFilters.asReadonly();
    setSavedSelectionFilters(savedSelectionFilters: IAccessRightSelectionFiltersCollection[]): void {
        this._savedSelectionFilters.set(savedSelectionFilters);
    }

    private _selectedFiltersCollection = signal<IAccessRightSelectionFiltersCollection | undefined>(undefined);
    readonly selectedFiltersCollection = this._selectedFiltersCollection.asReadonly();
    setSelectedFiltersCollection(value: IAccessRightSelectionFiltersCollection): void {
        this._selectedFiltersCollection.set(value);
    }

    private readonly _accessRightFilterCollection = signal<AccessRightFilterCollectionModel | undefined>(undefined);
    readonly accessRightFilterCollection = this._accessRightFilterCollection.asReadonly();
    setAccessRightFilterCollection(accessRightFilterCollection: AccessRightFilterCollectionModel): void {
        this._accessRightFilterCollection.set(accessRightFilterCollection);
    }

    private readonly _selectedLanguageId = signal<number>(this.fiContentLanguageService.getDefaultContentLanguageId());
    readonly selectedLanguageId = this._selectedLanguageId.asReadonly();
    setSelectedLanguageId(value: number): void {
        this._selectedLanguageId.set(value);
    }

    private readonly _initialQuizData = signal<IQuizEditItem | undefined>(undefined);
    readonly initialQuizData = this._initialQuizData.asReadonly();
    setInitialQuizData(quizData: IQuizEditItem): void {
        this._initialQuizData.set(quizData);
    }

    private _currentQuizData = signal<IQuizEditItem | undefined>(undefined);
    readonly currentQuizData = this._currentQuizData.asReadonly();
    setCurrentQuizData(quizData: IQuizEditItem): void {
        this._currentQuizData.set(cloneDeep(quizData));
    }
    readonly quizConnectedOnboardings = computed<INameIdObj[]>(
        () => this.currentQuizData()?.ConnectedOnboardings ?? [],
    );

    updateCurrentQuizData(updater: (current: IQuizEditItem) => IQuizEditItem): void {
        this._currentQuizData.update(updater);
    }

    private readonly _isQuizSharingSettingsSaved = signal<boolean>(false);
    readonly isQuizSharingSettingsSaved = this._isQuizSharingSettingsSaved.asReadonly();
    setIsQuizSharingSettingsSaved(isQuizSharingSettingsSaved: boolean): void {
        this._isQuizSharingSettingsSaved.set(isQuizSharingSettingsSaved);
    }

    private readonly _triggerQuizSharingSettingsSave = signal(0);
    readonly onQuizSharingSettingsSaveTrigger = this._triggerQuizSharingSettingsSave.asReadonly();
    triggerQuizSharingSettingsSave(): void {
        this._triggerQuizSharingSettingsSave.update((prev) => prev + 1);
    }

    private readonly _scrollToQuestionCard = signal<number | undefined>(undefined);
    readonly scrollToQuestionCard = this._scrollToQuestionCard.asReadonly();
    setScrollToQuestionCard(questionCardIndex: number): void {
        this._scrollToQuestionCard.set(questionCardIndex);
    }

    resetQuizEditorStoreData(): void {
        this.setInitialQuizData(undefined);
        this.setCurrentQuizData(undefined);
        this.setFormFailedValidation(false);
        this.setSelectedTabIndex(FiQuizEditorTabIndex.QuizEditor);
        this.setSavedSelectionFilters([]);
        this.setAccessRightFilterCollection(undefined);
        this.setSelectedLanguageId(this.fiContentLanguageService.getDefaultContentLanguageId());
        this.setSelectedFiltersCollection(undefined);
        this.setScrollToQuestionCard(undefined);
        this._triggerQuizSharingSettingsSave.set(0);
        this._isQuizSharingSettingsSaved.set(false);
    }
}
